import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getAvatarEntry } from 'shared/avatars/avatarRegistry'
import { studentsStore } from 'pages/Students/model/studentsStore'
import s from './styles/InitialAvatar.module.scss'

const avatarColors = [
	'#6C5CE7', // purple
	'#00B894', // green
	'#0984E3', // blue
	'#E17055', // orange
	'#D63031', // red
	'#E84393', // pink
	'#2D3436', // dark gray
	'#FDCB6E', // yellow
]

const getInitials = name => {
	if (!name) return '?'
	return name
		.split(' ')
		.filter(Boolean)
		.map(word => word[0])
		.join('')
		.toUpperCase()
}

const getAvatarColor = name => {
	const value = name || ''
	let hash = 0
	for (let i = 0; i < value.length; i++) {
		hash = value.charCodeAt(i) + ((hash << 5) - hash)
	}
	const index = Math.abs(hash) % avatarColors.length
	return avatarColors[index]
}

/**
 * Display priority:
 * 1) photoUrl + avatarSource === 'photo' (or explicit photoUrl prop) → photo (onError → animal)
 * 2) avatarKey in registry → local animal (onError → initials)
 * 3) initials
 *
 * Resolves missing avatar fields from `student` prop or `studentsStore` via `studentId`.
 * Never renders an empty avatar.
 */
const InitialAvatar = observer(
	({
		name,
		size = 48,
		className = '',
		avatarKey,
		avatarSource,
		photoUrl,
		studentId,
		student,
		onClick,
	}) => {
		const [photoFailed, setPhotoFailed] = useState(false)
		const [animalFailed, setAnimalFailed] = useState(false)
		const isClickable = typeof onClick === 'function'

		const storeStudent = studentId
			? studentsStore.getStudentById(studentId)
			: null

		const resolvedKey =
			avatarKey ?? student?.avatarKey ?? storeStudent?.avatarKey ?? null
		// Explicit photoUrl from the caller implies photo unless avatarSource overrides it.
		const resolvedSource =
			avatarSource ??
			(photoUrl ? 'photo' : undefined) ??
			student?.avatarSource ??
			storeStudent?.avatarSource ??
			null
		const resolvedPhoto =
			photoUrl || student?.photoUrl || storeStudent?.photoUrl || ''
		const resolvedName = name || student?.name || storeStudent?.name || ''

		useEffect(() => {
			setPhotoFailed(false)
		}, [resolvedPhoto, resolvedSource])

		useEffect(() => {
			setAnimalFailed(false)
		}, [resolvedKey])

		const initials = getInitials(resolvedName)
		const bgColor = getAvatarColor(resolvedName)
		const animalEntry = getAvatarEntry(resolvedKey)

		const showPhoto =
			resolvedSource === 'photo' && Boolean(resolvedPhoto) && !photoFailed
		const showAnimal = !showPhoto && Boolean(animalEntry) && !animalFailed

		const boxStyle = {
			width: size,
			height: size,
			fontSize: size * 0.4,
			backgroundColor: showPhoto || showAnimal ? 'transparent' : bgColor,
		}

		const rootClassName = [className, s.avatar, isClickable ? s.clickable : '']
			.filter(Boolean)
			.join(' ')

		const content = showPhoto ? (
			<img
				className={s.image}
				src={resolvedPhoto}
				alt=""
				width={size}
				height={size}
				onError={() => setPhotoFailed(true)}
			/>
		) : showAnimal ? (
			<picture className={s.picture}>
				<source type="image/webp" srcSet={animalEntry.webp} />
				<img
					className={s.image}
					src={animalEntry.png}
					alt=""
					width={size}
					height={size}
					onError={() => setAnimalFailed(true)}
				/>
			</picture>
		) : (
			initials
		)

		if (isClickable) {
			return (
				<button
					type="button"
					className={rootClassName}
					style={boxStyle}
					onClick={onClick}
					aria-label={`Open ${resolvedName || 'student'} profile`}
				>
					{content}
				</button>
			)
		}

		return (
			<div className={rootClassName} style={boxStyle}>
				{content}
			</div>
		)
	},
)

export default InitialAvatar
